<script setup lang="ts">

import { onMounted, ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import axiosApiClient from '@/axios.ts';
import { IconCircleCheck, IconLabel, IconLayoutList, IconMail, IconPhone } from '@tabler/icons-vue';
import { useUserStore } from '@/stores/user';


const t = useI18n().t;
const isbusy = ref(false);
const userStore = useUserStore();

interface Entity {
    space: 0,
    qty: 0,
    ratio: 0,
    total_space: 0,
    price: 460,
    total_amount: 0,
    notes: '',
}
interface Estimation {
    id: number;
    name: string;
    email: string;
    phone: string;
    is_approved: string;
    completed_date: string;
    description: string;
    total_amount: number;
    total_space: number;
    status: string;
}


const estimations = ref<Estimation[]>([]);
const fetchEstimations = async () => {
    isbusy.value = true;
    const response = await axiosApiClient.get('/api/estimations');
    estimations.value = response.data;
    isbusy.value = false;
};

const searchQuery = ref('');

const filteredEstimations = computed(() => {
    if (!searchQuery.value) {
        return estimations.value;
    }
    // Filter estimations based on the search query
    if(estimations.value.length === 0) {
        return [];
    }
    return estimations.value.filter((estimation) => {
        return estimation.name.toLowerCase().includes(searchQuery.value.toLowerCase());
    });
});

const selectedEstimation = ref<Estimation>({
    id: 0,
    name: '',
    email: '',
    phone: '',
    is_approved: 'N',
    completed_date: '',
    description: '',
    total_amount: 0,
    total_space: 0,
    status: 'pending',
});
const selectedEstimationComponents = ref<Entity[]>([]);
const showDetailsModal = ref(false);
onMounted(() => {
    fetchEstimations();
});
const openDetailsModal = (id: number) => {
    // Logic to show the details modal
    const estimation = estimations.value.find((est) => est.id === id);
    if (estimation) {
        selectedEstimation.value = estimation;
        selectedEstimationComponents.value = JSON.parse(estimation.description);
        selectedEstimation.value.total_amount = 0;
        selectedEstimation.value.total_space = 0;
        showDetailsModal.value = true;

        setTimeout(() => {
            const total_inputs = document.querySelectorAll('input[name="value_total"]');
            const total_space_inputs = document.querySelectorAll('input[name="space_total"]');
            total_inputs.forEach((input) => {
                (input as HTMLInputElement).addEventListener('change', calculateTotalAmount);
            });
            total_space_inputs.forEach((input) => {
                (input as HTMLInputElement).addEventListener('change', calculateTotalAmount);
            });
            calculateTotalAmount();
        }, 1000);
    }
    else {
        console.error('Estimation not found');
    }
};
const confirmEstimation = async () => {
    if (selectedEstimation.value) {
        const response = await axiosApiClient.post('/api/estimations/confirm', {
            id: selectedEstimation.value.id,
            description: JSON.stringify(selectedEstimationComponents.value),
            is_approved: 'Y',
            status: 'confirmed',
            user_id: userStore.user.id,
        });
        if (response.data.status === 'success') {
            showDetailsModal.value = false;
            fetchEstimations();
        }
    }
};
//calculate total amount for components of selected estimation
const calculateTotalAmount = () => {
    const total_inputs = document.querySelectorAll('input[name="value_total"]');
    const total_space_inputs = document.querySelectorAll('input[name="space_total"]');

    let total_amount = 0;
    let total_space = 0;
    total_inputs.forEach((input) => {
        const value = parseFloat((input as HTMLInputElement).value);
        if (!isNaN(value)) {
            total_amount += value;
        }
    });
    total_space_inputs.forEach((input) => {
        const value = parseFloat((input as HTMLInputElement).value);
        if (!isNaN(value)) {
            total_space += value;
        }
    });
    selectedEstimation.value.total_amount = total_amount;
    selectedEstimation.value.total_space = total_space;
};
</script>
<template>
    <admin-layout>
        <!--list estimations-->
        <div class="container mx-auto">
            <h1 class="text-2xl font-bold mb-4">{{ t('Estimations') }}</h1>
            <div>
                <!--search-->
                <input type="text" v-model="searchQuery" :placeholder="t('Search') + '...'"
                    class="border p-2 rounded mb-4" />
            </div>
            <table class="min-w-full bg-white border border-gray-200 table-striped table-hover text-center">
                <thead>
                    <tr>
                        <th class="py-2 px-4 border-b">#</th>
                        <th class="py-2 px-4 border-b">{{ t('Name') }}</th>
                        <th class="py-2 px-4 border-b">{{ t('Email') }}</th>
                        <th class="py-2 px-4 border-b">{{ t('Phone') }}</th>
                        <th class="py-2 px-4 border-b">{{ t('Status') }}</th>
                        <th class="py-2 px-4 border-b">{{ t('Approved') }}</th>
                        <th class="py-2 px-4 border-b">{{ t('Completion Date') }}</th>
                        <th class="py-2 px-4 border-b"></th>
                    </tr>
                </thead>
                <tbody>
                    <!-- Loop through estimations and display them here -->
                    <tr v-for="estimation in filteredEstimations" :key="estimation.id">
                        <td class="py-2 px-4 border-b">{{ estimation.id }}</td>
                        <td class="py-2 px-4 border-b">{{ estimation.name }}</td>
                        <td class="py-2 px-4 border-b">{{ estimation.email }}</td>
                        <td class="py-2 px-4 border-b">{{ estimation.phone }}</td>
                        <td class="py-2 px-4 border-b">{{ t(estimation.status) }}</td>
                        <td class="py-2 px-4 border-b">{{ estimation.is_approved }}</td>
                        <td class="py-2 px-4 border-b">{{ estimation.completed_date }}</td>
                        <td class="py-2 px-4 border-b">
                            <a href="#!" @click="openDetailsModal(estimation.id)" class="text-blue-500 hover:underline">
                                {{ t('Details') }}
                            </a>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <!-- Show Estimation Form to review and confirm or cancel -->
        <div class="modal" v-if="showDetailsModal">
            <div class="modal-content">
                <span class="close" @click="showDetailsModal = false">&times;</span>
                <h3>{{ t('Estimation Details') }} #{{ selectedEstimation.id }}</h3>
                <!-- Display estimation details here -->
                <div class="flex gap-2">
                    <p class="flex gap-1">
                        <IconLabel stroke={2} />{{ t('Name') }}: {{ selectedEstimation.name }}
                    </p>
                    <p class="flex gap-1">
                        <IconMail stroke={2} />{{ t('Email') }}: {{ selectedEstimation.email }}
                    </p>
                    <p class="flex gap-1">
                        <IconPhone stroke={2} />{{ t('Phone') }}: {{ selectedEstimation.phone }}
                    </p>
                    <p class="flex gap-1">
                        <IconCircleCheck stroke={2} />{{ t('Completion Date') }}: 
                        {{ selectedEstimation.completed_date}}
                    </p>
                </div>
                <!-- Add more details as needed -->
                <h3>{{ t('Description') }}</h3>
                <!--Components-->
                <ul>
                    <li v-for="(entity, key) in selectedEstimationComponents" :key="key">
                        <strong class="flex gap-1 my-1">
                            <IconLayoutList stroke={2} /><b>{{ t(key) }}</b>
                        </strong>
                        <ul class="border p-1">
                            <div class="flex gap-2 flex-wrap m-0">
                                <div class="flex flex-col">
                                    <label for="">{{ t('Quantity') }}</label>
                                    <input type="number" v-model="entity.qty" class="border p-2 rounded mb-4" />
                                </div>
                                <div class="flex flex-col">
                                    <label for="">{{ t('Space') }}</label>
                                    <input type="number" v-model="entity.space" class="border p-2 rounded mb-4" />
                                </div>
                                <div class="flex flex-col">
                                    <label for="">{{ t('Ratio') }}</label>
                                    <input type="number" v-model="entity.ratio" class="border p-2 rounded mb-4" />
                                </div>
                                <div class="flex flex-col">
                                    <label for="">{{ t('Total Space') }}</label>
                                    <input type="number" v-model="entity.total_space" name="space_total"
                                        class="border p-2 rounded mb-4" />
                                </div>
                                <div class="flex flex-col">
                                    <label for="">{{ t('Price') }}</label>
                                    <input type="number" v-model="entity.price" class="border p-2 rounded mb-4" />
                                </div>
                                <div class="flex flex-col">
                                    <label for="">{{ t('Total Amount') }}</label>
                                    <input type="number" v-model="entity.total_amount" name="value_total"
                                        class="border p-2 rounded mb-4" />
                                </div>
                            </div>
                        </ul>
                    </li>
                </ul>
                <div>
                    <!--totals-->
                    <h3>{{ t('Totals') }}</h3>
                    <div class="flex gap-2 flex-wrap m-0">
                        <div class="flex flex-col">
                            <label for="">{{ t('Total Space') }}</label>
                            <input type="number" v-model="selectedEstimation.total_space" name="space_total"
                                class="border p-2 rounded mb-4" />
                        </div>
                        <div class="flex flex-col">
                            <label for="">{{ t('Total Amount') }}</label>
                            <input type="number" v-model="selectedEstimation.total_amount" name="value_total"
                                class="border p-2 rounded mb-4" />
                        </div>
                    </div>
                </div>
                <div class="flex justify-end mt-4 gap-3">
                    <button @click="showDetailsModal = false" class="bg-gray-500 text-white px-4 py-2 rounded mr-2">
                        {{ t('Close') }}
                    </button>
                    <button @click="confirmEstimation" class="bg-blue-500 text-white px-4 py-2 rounded"
                        v-if="selectedEstimation.is_approved == 'N'">
                        {{ t('Confirm') }}
                    </button>
                </div>
            </div>
        </div>
        <!-- End of modal -->
    </admin-layout>
</template>



<style scoped lang="scss">
.modal {
    display: flex;
    justify-content: center;
    align-items: center;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 1000;
    transition: all 0.3s ease;

    &.fade {
        opacity: 0;
        visibility: hidden;
    }

    &.show {
        opacity: 1;
        visibility: visible;
    }

    .modal-content {
        background-color: white;
        padding: 20px;
        border-radius: 5px;
        width: 80%;
        max-width: 80%;
        margin: auto;
        position: relative;

        .close {
            position: absolute;
            top: 10px;
            right: 20px;
            font-size: 24px;
            cursor: pointer;
        }
    }
}
</style>